import { Warning } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Link,
  Stack,
  TableCell,
  TableContainer,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import { useMode } from "../mode/ModeContext";
import { useModeMetrics } from "../mode/useModeMetrics";
import MortgageSimulator from "../simulator";
import { yearsAndMonths } from "../utils";
import Insights from "./Insights";
import SimulationMenu from "./SimulationMenu";
import SimulationChart from "./SimulationChart";
import SimulationSchedule from "./SimulationSchedule";
import SavingsAccountsTable from "./SavingsAccountsTable";

function SummaryTable(props: { sim: MortgageSimulator }) {
  const c = useCurrencyFormatter();

  return (
    <TableContainer>
      <Box
        component="table"
        sx={{ width: "100%", mt: 2, borderCollapse: "collapse" }}
      >
        <Box component="tbody">
          <Box component="tr">
            <TableCell component="th" scope="row">
              Principal
            </TableCell>
            <TableCell
              align="center"
              sx={{ width: "1%", whiteSpace: "nowrap", pr: 0 }}
            ></TableCell>
            <TableCell align="right" sx={{ pl: 0 }}>
              {c(props.sim.principal)}
            </TableCell>
          </Box>

          <Box component="tr">
            <TableCell component="th" scope="row">
              Total Interest Paid
            </TableCell>
            <TableCell
              align="center"
              sx={{ width: "1%", whiteSpace: "nowrap", pr: 0 }}
            >
              +
            </TableCell>
            <TableCell align="right" sx={{ pl: 0 }}>
              {c(props.sim.totalInterestPaid)}
            </TableCell>
          </Box>

          {props.sim.totalInterestEarned > 0 && (
            <Box component="tr">
              <TableCell component="th" scope="row">
                Total Interest Earned
              </TableCell>
              <TableCell
                align="center"
                sx={{ width: "1%", whiteSpace: "nowrap", pr: 0 }}
              >
                &minus;
              </TableCell>
              <TableCell align="right" sx={{ pl: 0 }}>
                {c(props.sim.totalInterestEarned)}
              </TableCell>
            </Box>
          )}

          <Box component="tr">
            <TableCell component="th" scope="row">
              <strong>Total Cost of Mortgage</strong>
            </TableCell>
            <TableCell
              align="center"
              sx={{ width: "1%", whiteSpace: "nowrap", pr: 0 }}
            >
              =
            </TableCell>
            <TableCell align="right" sx={{ pl: 0 }}>
              <strong>{c(props.sim.totalCostOfMortgage)}</strong>
            </TableCell>
          </Box>
        </Box>
      </Box>
    </TableContainer>
  );
}

function SimulationBreadcrumbs(props: { onClick: () => void; idx: number }) {
  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <IconButton color="primary" onClick={props.onClick}>
        <ArrowBackIcon />
      </IconButton>
      <Breadcrumbs aria-label="breadcrumb">
        <Link
          color="inherit"
          underline="hover"
          href="#"
          onClick={props.onClick}
        >
          Simulations
        </Link>
        <Typography color="text.primary">#{props.idx + 1}</Typography>
      </Breadcrumbs>
    </Stack>
  );
}

function SimulationWarnings(props: { strings: string[] }) {
  return (
    <>
      {props.strings.length > 0 && (
        <Tooltip title={props.strings.join("\n")} arrow>
          <IconButton size="large" color="warning">
            <Warning />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

function SimulationControls(props: {
  smallScreen: boolean;
  idx: number;
  handleEdit?: (idx: number) => void;
  handleDuplicate?: (idx: number) => void;
  handleDelete?: (idx: number) => void;
  warningMessages: string[];
  deleteDisabled?: boolean;
}) {
  return (
    <Stack direction="row" spacing={1}>
      <SimulationWarnings strings={props.warningMessages} />
      {props.smallScreen ? (
        <SimulationMenu
          idx={props.idx}
          handleEdit={props.handleEdit}
          handleDuplicate={props.handleDuplicate}
          handleDelete={props.handleDelete}
          deleteDisabled={props.deleteDisabled}
        />
      ) : (
        <>
          {props.handleEdit && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => props.handleEdit!(props.idx)}
            >
              Edit
            </Button>
          )}
          {props.handleDuplicate && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ContentCopyIcon />}
              onClick={() => props.handleDuplicate!(props.idx)}
            >
              Duplicate
            </Button>
          )}
          {props.handleDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => props.handleDelete!(props.idx)}
              disabled={props.deleteDisabled}
            >
              Delete
            </Button>
          )}
        </>
      )}
    </Stack>
  );
}

export default function SimulationPage({
  sim,
  moreExpensive,
  idx,
  handleEdit,
  handleDuplicate,
  handleDelete,
  toggleExpanded,
  deleteDisabled = false,
}: {
  sim: MortgageSimulator;
  moreExpensive: number | null;
  idx: number;
  handleEdit?: (idx: number) => void;
  handleDuplicate?: (idx: number) => void;
  handleDelete?: (idx: number) => void;
  toggleExpanded: () => void;
  deleteDisabled?: boolean;
}) {
  const c = useCurrencyFormatter();
  const { Primary: PrimaryMetric, Secondary: SecondaryMetric } =
    useModeMetrics();

  const { mode } = useMode();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    window.scrollTo({ top: 0 });
  });

  return (
    <Box sx={{ px: 2 }}>
      {mode !== "singleSimulation" && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={{ xs: 1, sm: 3 }}
        >
          <SimulationBreadcrumbs onClick={toggleExpanded} idx={idx} />
          <SimulationControls
            smallScreen={isSmallScreen}
            idx={idx}
            handleEdit={handleEdit}
            handleDuplicate={handleDuplicate}
            handleDelete={handleDelete}
            warningMessages={sim.warningMessages}
            deleteDisabled={deleteDisabled}
          />
        </Stack>
      )}

      <Box sx={{ pt: mode !== "singleSimulation" ? 0 : 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography variant="h4" fontWeight="bold">
              <PrimaryMetric sim={sim} />
            </Typography>
          </Box>
          {mode === "singleSimulation" && (
            <SimulationControls
              smallScreen={isSmallScreen}
              idx={idx}
              handleEdit={handleEdit}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              warningMessages={sim.warningMessages}
              deleteDisabled={deleteDisabled}
            />
          )}
        </Stack>
        <Typography variant="subtitle1" color="text.secondary" mb={4}>
          <SecondaryMetric sim={sim} />
        </Typography>
      </Box>

      {(moreExpensive !== null || sim.additionalCapitalRepayments) && (
        <Box sx={{ pb: 3 }}>
          <Insights moreExpensive={moreExpensive} sim={sim} />
        </Box>
      )}

      <Grid container spacing={3} mb={5}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {sim.interestOnly ? "Interest-only m" : "M"}ortgage paid off in{" "}
                <strong>{yearsAndMonths(sim.numberOfMonths, true)}</strong>
                {sim.additionalCapitalRepayments && (
                  <>
                    , which is{" "}
                    <strong>
                      {yearsAndMonths(sim.monthsPaidOffSooner!, true)}
                    </strong>{" "}
                    sooner than expected due to additional capital repayments
                  </>
                )}
                .
              </Typography>
              {sim.additionalCapitalRepayments ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <strong>
                    {sim.mortgage.ledger.entries.reduce(
                      (count, entry) =>
                        entry.additionalCapitalRepayments > 0
                          ? count + 1
                          : count,
                      0,
                    )}
                  </strong>{" "}
                  additional capital repayments totaling{" "}
                  <strong>
                    {c(sim.mortgage.ledger.totalAcrAmount(), true)}
                  </strong>{" "}
                  were made towards the mortgage.
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  No additional capital repayments were made
                  {sim.savingsAccounts.length === 0 &&
                    " and no savings or investment accounts were linked"}
                  .
                </Typography>
              )}
              <SummaryTable sim={sim} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                {sim.savingsAccounts.length > 0 ||
                sim.additionalCapitalRepayments
                  ? "Mortgage, Savings & Investments Growth Over Time"
                  : "Mortgage Balance Over Time"}
              </Typography>
              <SimulationChart sim={sim} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {sim.additionalCapitalRepayments && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Additional Capital Repayments
          </Typography>

          {sim.additionalCapitalRepayments ? (
            <>
              <Typography variant="body2" color="text.secondary">
                The monthly budget of{" "}
                <strong>{c(sim.maxAffordable, true)}</strong> (minus the
                required monthly payment) was allocated towards additional
                capital repayments. It was assumed that only one additional
                capital repayment could be made per year, up to{" "}
                <strong>
                  {sim.additionalCapitalRepaymentPercentageLimit}%
                </strong>{" "}
                of the mortgage balance at the time of repayment.{" "}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Upon each additional capital repayment, the mortgage term was{" "}
                {sim.mortgage.reduceTermOnAdditionalCapitalRepayment
                  ? "reduced, keeping the monthly payment the same"
                  : "kept the same, reducing the monthly payment. At the end of each recurring deal, the mortgage was recalculated to ensure the desired monthly payment was maintained"}
                .{" "}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                These repayments were <em>(a)</em> spread out evenly over each
                recurring deal of{" "}
                <Tooltip
                  title={`Paid in months ${sim.mortgage.dealDistributedAdditionalCapitalRepaymentMonths
                    .slice(0, -1)
                    .join(", ")
                    .replace(
                      /,([^,]*)$/,
                      " and$1",
                    )} (accounting for an extra payment at the end of the deal)`}
                >
                  <strong>{sim.dealLength}&nbsp;months</strong>
                </Tooltip>{" "}
                <strong>
                  (
                  {sim.mortgage.ledger.nAcrsWithType(
                    "spread-additional-capital-repayments-over-deal",
                  )}
                  &nbsp;repayments)
                </strong>{" "}
                and <em>(b)</em> paid once the maximum allowable repayment
                amount was reached{" "}
                <strong>
                  (
                  {sim.mortgage.ledger.nAcrsWithType(
                    "saved-maximum-additional-capital-repayment-amount",
                  )}
                  &nbsp;repayments)
                </strong>
                , whichever came first. At the end of each recurring deal, the
                funds in the ACR Reserve Account were put towards an additional
                capital repayment excluded from the yearly limit{" "}
                <strong>
                  ({sim.mortgage.ledger.nAcrsWithType("between-fixed-deals")}
                  &nbsp;repayments)
                </strong>
                .
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                In total,{" "}
                <strong>{c(sim.mortgage.ledger.totalAcrAmount(), true)}</strong>{" "}
                was paid in additional capital repayments over the course of the
                simulation.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No additional capital repayments were made.
            </Typography>
          )}
        </Box>
      )}

      {(sim.savingsAccounts.length > 0 || sim.additionalCapitalRepayments) && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            Savings & Investments
          </Typography>
          {sim.additionalCapitalRepayments &&
            (sim.savingsInterestRate && sim.savingsInterestRate > 0 ? (
              <Typography variant="body2" color="text.secondary">
                An ACR Reserve Account was created to hold additional capital
                repayments, earning an interest rate of{" "}
                <strong>{sim.savingsInterestRate}%</strong>.
                {sim.saveAtStart ? (
                  <>
                    {" "}
                    The monthly budget of{" "}
                    <strong>{c(sim.maxAffordable, true)}</strong> was added to
                    the account at the start of each month.
                  </>
                ) : (
                  <>
                    {" "}
                    The monthly budget of{" "}
                    <strong>{c(sim.maxAffordable, true)}</strong> was added to
                    the account at the <em>end</em> of each month after
                    subtracting the mortgage payment.
                  </>
                )}
                <>
                  {" "}
                  This account earned a total of{" "}
                  <strong>
                    {c(sim.totalInterestEarnedInReserveAccount(), true)}
                  </strong>{" "}
                  in interest over the course of the simulation.
                </>
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Additional capital repayments were made, but no interest was
                earned on the reserve account.
              </Typography>
            ))}

          <Box sx={{ mt: 2 }}>
            <SavingsAccountsTable sim={sim} />
          </Box>

          {sim.savingsAccounts.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No additional savings or investment accounts were linked.
            </Typography>
          )}
        </Box>
      )}

      <SimulationSchedule sim={sim} />
    </Box>
  );
}
